import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, MessageSquare, Phone, MapPin, Send, HelpCircle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Footer from "@/components/Footer";

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success("Message sent! We'll get back to you within 24 hours.");
    setFormData({ name: "", email: "", subject: "", message: "" });
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50 flex flex-col">
      {/* Header - Transparent Style */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/logo-main.png" 
              alt="RecoveryMatch" 
              className="w-10 h-10 rounded-xl"
            />
            <span className="font-bold text-xl">
              <span className="text-[#1C3A7A]">Recovery</span>
              <span className="text-[#FAB710]">Match</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-[#1C3A7A] transition-colors">Features</button>
            <button onClick={() => navigate('/founder')} className="hover:text-[#1C3A7A] transition-colors">About</button>
            <button onClick={() => navigate('/faq')} className="hover:text-[#1C3A7A] transition-colors">FAQ</button>
            <button onClick={() => navigate('/contact')} className="text-[#1C3A7A] font-semibold transition-colors">Contact</button>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/login')}
              className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition px-4 py-2 hidden sm:block"
            >
              Log In
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="bg-[#FAB710] text-white px-5 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-200 hover:scale-105 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-28 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-[#1C3A7A] mb-4">How Can We Help?</h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Our support team is here to help you on your journey. Reach out anytime.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl shadow-lg p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-[#1C3A7A] mb-6 flex items-center gap-2">
                  <MessageSquare className="w-6 h-6 text-[#FAB710]" />
                  Send Us a Message
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="h-12 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="h-12 rounded-xl"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                    <div className="relative">
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full h-12 px-4 bg-white border border-gray-200 rounded-xl appearance-none text-gray-700 focus:ring-[#FAB710] focus:border-[#FAB710]"
                        required
                      >
                        <option value="">Select a topic</option>
                        <option value="account">Account Issues</option>
                        <option value="billing">Billing & Subscriptions</option>
                        <option value="report">Report a User</option>
                        <option value="bug">Bug Report</option>
                        <option value="feedback">Feedback & Suggestions</option>
                        <option value="other">Other</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help..."
                      rows={5}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 focus:ring-[#FAB710] focus:border-[#FAB710] resize-none"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-[#FAB710] to-[#FAB710] hover:shadow-lg hover:shadow-orange-200 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Contact Info & Quick Links */}
            <div className="space-y-6">
              {/* Contact Cards */}
              <div className="bg-white rounded-3xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-[#1C3A7A] mb-4">Contact Info</h3>
                <div className="space-y-4">
                  <a href="mailto:support@recoverymatch.com" className="flex items-center gap-3 text-gray-600 hover:text-[#FAB710] transition">
                    <div className="w-10 h-10 bg-[#FAB710]/10 rounded-xl flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#FAB710]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium">support@recoverymatch.com</p>
                    </div>
                  </a>
                  
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="w-10 h-10 bg-[#1C3A7A]/10 rounded-xl flex items-center justify-center">
                      <Phone className="w-5 h-5 text-[#1C3A7A]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone (Coming Soon)</p>
                      <p className="font-medium text-gray-400">1-800-RECOVER</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Headquarters</p>
                      <p className="font-medium">Austin, Texas</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-gradient-to-br from-[#1C3A7A] to-[#0f2550] rounded-3xl p-6 text-white">
                <h3 className="font-bold mb-2">Response Time</h3>
                <p className="text-white/80 text-sm mb-4">
                  We typically respond within 24 hours. For urgent matters, please indicate "URGENT" in your subject line.
                </p>
                <div className="flex items-center gap-2 text-[#FAB710]">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium">Support team online</span>
                </div>
              </div>
            </div>
          </div>

          {/* Link to FAQ Page */}
          <div className="mt-12">
            <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2d4a8a] rounded-2xl p-8 text-center text-white">
              <HelpCircle className="w-12 h-12 mx-auto mb-4 text-[#FAB710]" />
              <h2 className="text-2xl font-bold mb-2">Have more questions?</h2>
              <p className="text-white/80 mb-6">Browse our comprehensive FAQ for answers to common questions.</p>
              <button
                onClick={() => navigate('/faq')}
                className="px-6 py-3 bg-[#FAB710] text-[#1C3A7A] font-semibold rounded-xl hover:bg-[#f5c542] transition"
              >
                View All FAQs
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
